import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import { gql } from "@apollo/client";
import type { SyncablePriceListFacility } from "elephant-web/gql";
import { useMemo } from "react";
import type { RxCollection, RxDatabase, WithDeleted } from "rxdb";
import type { PriceListFacility as RxPriceListFacility } from "../collections/price-list-facility";
import type { RxDBCollections } from "../provider";
import {
  SyncPriceListFacilityPullDocument,
  type WholeSyncablePriceListFacilityFragment,
} from "./__generated__/price-list-facility.gql";
import {
  type ReplicationPullConfig,
  useGqlReplication,
} from "./use-gql-replication";

export const useReplicatePriceListFacility = ({
  db,
  collection,
}: {
  db?: RxDatabase<RxDBCollections>;
  collection?: RxCollection<RxPriceListFacility>;
}) => {
  const [enabled] = useFeatureFlag<boolean>(featureFlags.billing.priceListRxdb);

  const pull = useMemo<
    ReplicationPullConfig<WholeSyncablePriceListFacilityFragment> | undefined
  >(() => {
    if (!enabled) return undefined;
    return {
      query: SyncPriceListFacilityPullDocument,
      buildInputVariables: async (checkpoint, batchSize) => ({
        input: {
          limit: batchSize,
          checkpoint,
        },
      }),
      buildPullCheckpoint: async (doc) => ({
        id: doc.id,
        updatedAt: doc.meta.updatedAt,
      }),
      reSyncInterval: { minutes: 5 },
    };
  }, [enabled]);

  return useGqlReplication<
    RxPriceListFacility,
    SyncablePriceListFacility,
    null
  >({
    db,
    collection,
    pull,
    mapGqlToRx: mapGqlPriceListFacilityToRxPriceListFacility,
    batchSize: 100,
    metadataLocalStorageKeySuffix: "price-list-facility",
  });
};

export const mapGqlPriceListFacilityToRxPriceListFacility = (
  priceListFacility: WholeSyncablePriceListFacilityFragment,
): WithDeleted<RxPriceListFacility> => ({
  id: priceListFacility.id,
  organisationId: priceListFacility.organisationId,
  priceListId: priceListFacility.priceListId ?? null,
  linkedOrganisationPriceListId:
    priceListFacility.linkedOrganisationPriceListId ?? null,
  meta: {
    createdAt: priceListFacility.meta.createdAt,
    createdByUserId: priceListFacility.meta.createdByUserId,
    updatedAt: priceListFacility.meta.updatedAt,
    updatedByUserId: priceListFacility.meta.updatedByUserId,
  },
  _deleted: priceListFacility.meta.deleted,
});

gql`
  fragment WholeSyncablePriceListFacility on SyncablePriceListFacility {
    id
    organisationId
    priceListId
    linkedOrganisationPriceListId
    meta {
      createdAt
      createdByUserId
      updatedAt
      updatedByUserId
      deleted
    }
  }
`;

gql`
  query SyncPriceListFacilityPull($input: PullPriceListFacilityInput!) {
    pullPriceListFacility(input: $input) {
      checkpoint {
        id
        updatedAt
      }
      documents {
        ...WholeSyncablePriceListFacility
      }
    }
  }
`;
